import React from 'react'

import { OrdersContext } from 'contexts/OrdersContext'
import { DcFlexBox, DcTabs, Section, SectionContent } from 'pixel-base'
import { useApi } from 'shared/hooks'

import { get } from 'utils/api-routes.util'
import { StatusCancelledIcon } from 'utils/icons/status-cancelled-icon'
import { StatusCompleteIcon } from 'utils/icons/status-complete-icon'
import { StatusNewIcon } from 'utils/icons/status-new-icon'
import { StatusPostDeliveryIcon } from 'utils/icons/status-post-delivery-icon'
import { StatusProcessingIcon } from 'utils/icons/status-processing-icon'
import { StatusReturnsIcon } from 'utils/icons/status-returns-icon'
import { StatusTrackingIcon } from 'utils/icons/status-tracking-icon'

interface IOrderStatus {
  color: string
  ems_status_id: string
  ems_status_label: string
  status_classification: string
  name: string
  id: string
  count: number
  is_post_ship: boolean
}

interface IRes {
  response: { data: IOrderStatus[] }
}

interface IStatusGroup {
  key: number
  children: IOrderStatus[]
  color: string
  name: string
  parent_count: number
}

export interface OrderFilters {
  store_id: number | null
  page: number | null
  page_size: number | null
  start_date: string | null
  end_date: string | null
  courier_id: number | null
  tag: string | null
  id: number | null
  status_classification_id: number | null
}

export interface IProps {
  filters: OrderFilters
  isVisible: boolean
  handleClearRows: () => void
  filterApplied: (key: string, value: string, enable: boolean) => void
  setFilters: (filters: OrderFilters) => void
  formatFiltersForAPI: (filters: OrderFilters) => any
  setIsVisible: (isVisible: boolean) => void
}

const OrderStatus = (props: IProps) => {
  const { state, setStateItem } = React.useContext(OrdersContext)
  const {
    handleClearRows,
    filterApplied,
    filters,
    setFilters,
    formatFiltersForAPI,
    isVisible,
    setIsVisible,
  } = props

  const [localState, setLocalState] = React.useState(state)
  const [activeTab, setActiveTab] = React.useState<string | null>(null)
  const [activeTabChildren, setActiveTabChildren] = React.useState<
    IOrderStatus[]
  >([])

  // Use the unified filter formatting function to ensure consistency with /orders API
  // Memoize filterFormat to prevent infinite re-renders
  const filterFormat = React.useMemo(() => {
    return formatFiltersForAPI(filters)
  }, [
    filters.store_id,
    filters.courier_id,
    filters.tag,
    filters.start_date,
    filters.end_date,
    filters.page,
    filters.id,
    filters.page_size,
    filters.status_classification_id,
    formatFiltersForAPI,
  ])

  useApi<IRes>(() => get('/orders/status/counts', filterFormat), {
    onSuccess: (res) => {
      if (res && res.data && setStateItem) {
        setStateItem('orderStatuses', res.data)
      }
    },
    deps: [filterFormat], // Add filterFormat as dependency to trigger API call on filter changes
  })

  const groupStatus = (orderStatuses?: IOrderStatus[]) => {
    const groups: Record<string, IStatusGroup> = {}
    if (orderStatuses || (localState && localState.orderStatuses)) {
      ;(
        orderStatuses ||
        (localState && localState.orderStatuses) ||
        []
      ).forEach((status) => {
        if (groups[status['status_classification']]) {
          groups[status['status_classification']].children.push(status)
          groups[status['status_classification']].parent_count +=
            status['count']
        } else {
          groups[status['status_classification']] = {
            key: Math.random(),
            children: [status],
            color: status['color'],
            name: status['status_classification'],
            parent_count: status['count'],
          }
        }
      })
    }

    const groupedStatuses: IStatusGroup[] = []
    Object.keys(groups).forEach((key) => {
      groupedStatuses.push(groups[key])
    })
    if (setStateItem) {
      setStateItem('groupedStatuses', groupedStatuses)
    }
    return groupedStatuses
  }

  const applyFilter = (key, value, enable = true) => {
    updateCurrentSelection(value, enable)
    if (filterApplied) {
      filterApplied(key, value, enable)
    }
  }

  const updateCurrentSelection = (status_id, enable) => {
    if (enable) {
      const selectedStatus = (localState?.orderStatuses || []).filter(
        (status) => {
          return status['id'] === status_id
        },
      )
      if (setStateItem) {
        setStateItem(
          'selectedStatus',
          selectedStatus.length ? selectedStatus[0] : null,
        )
      }
    } else {
      if (setStateItem) {
        setStateItem('selectedStatus', null)
      }
    }
  }

  const tab = localState?.groupedStatuses?.map((status) => {
    return {
      label: (
        <>
          {status.name === 'New' ? (
            <StatusNewIcon size={16} />
          ) : status.name === 'Processing' ? (
            <StatusProcessingIcon size={16} />
          ) : status.name === 'Tracking' ? (
            <StatusTrackingIcon size={16} />
          ) : status.name === 'Complete' ? (
            <StatusCompleteIcon size={16} />
          ) : status.name === 'Cancelled' ? (
            <StatusCancelledIcon size={16} />
          ) : status.name === 'Returns' ? (
            <StatusReturnsIcon size={16} />
          ) : status.name === 'Post Delivery' ? (
            <StatusPostDeliveryIcon size={16} />
          ) : (
            <></>
          )}
          &nbsp;
          {status.name || 'Untitled'} ({status.parent_count || 0})
        </>
      ),
      value: `${
        status.name === 'New'
          ? 1
          : status.name === 'Processing'
          ? 2
          : status.name === 'Tracking'
          ? 3
          : status.name === 'Complete'
          ? 4
          : status.name === 'Cancelled'
          ? 5
          : status.name === 'Returns'
          ? 6
          : status.name === 'Post Delivery'
          ? 7
          : 0
      }`,
    }
  })

  const tabs = [
    { label: 'All Status', value: null },
    ...(tab || []).sort(
      (a, b) => parseInt(a.value, 10) - parseInt(b.value, 10),
    ),
  ]

  React.useEffect(() => {
    const activeTabName =
      activeTab === '1'
        ? 'New'
        : activeTab === '2'
        ? 'Processing'
        : activeTab === '3'
        ? 'Tracking'
        : activeTab === '4'
        ? 'Complete'
        : activeTab === '5'
        ? 'Cancelled'
        : activeTab === '6'
        ? 'Returns'
        : activeTab === '7'
        ? 'Post Delivery'
        : ''

    if (activeTab) {
      const selectedStatus = groupStatus().filter((status) => {
        return status['name'] === activeTabName
      })
      setActiveTabChildren(selectedStatus[0]?.children || [])
    }
    if (activeTab == null) {
      setActiveTabChildren([])
    }
  }, [activeTab])

  const tabChildren = activeTabChildren?.map((status) => {
    return {
      label: `${status.name || 'Untitled'} (${status.count || 0})`,
      value: status.ems_status_id,
    }
  })

  React.useEffect(() => {
    groupStatus()
    applyFilter('id', filters['id'], true)
    if (filters.status_classification_id == null) setActiveTabChildren([])
  }, [filters])

  React.useEffect(() => {
    if (state) {
      setLocalState(state)
    }
  }, [state])

  React.useEffect(() => {
    if (state?.orderStatuses) {
      groupStatus(state.orderStatuses)
    }
  }, [state?.orderStatuses])

  React.useEffect(() => {
    if (tabChildren?.length > 0) {
      setTimeout(() => {
        setIsVisible(true)
      }, 300)
    } else {
      setIsVisible(false)
    }
  }, [tabChildren])

  React.useEffect(() => {
    if (filters.status_classification_id == null) {
      setActiveTab(null)
      if (setFilters) {
        setFilters({ ...filters, id: null })
      }
    }
  }, [filters.status_classification_id])

  return (
    <Section style={{ padding: '20px 20px 10px 20px' }}>
      <SectionContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '0px',
        }}
      >
        <DcFlexBox justifyContent="space-between" alignItems="center">
          <DcFlexBox gap="5px">
            <DcFlexBox
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <DcFlexBox
                gap="5px"
                justifyContent="space-between"
                flexDirection="column"
                padding="10px 10px 5px 10px"
              >
                <DcTabs
                  variant="pills"
                  size="sm"
                  tabs={tabs}
                  activeTab={
                    filters.status_classification_id
                      ? String(filters.status_classification_id)
                      : null
                  }
                  handleChange={(value) => {
                    setIsVisible(false)
                    setActiveTab(value)
                    setFilters({
                      ...filters,
                      status_classification_id: value ? Number(value) : null,
                      id: null,
                    })
                    handleClearRows()
                  }}
                />
              </DcFlexBox>
              <DcFlexBox
                style={{
                  width: isVisible ? '100%' : '0%',
                  height: isVisible ? 'auto' : '0px',
                  alignItems: 'center',
                  padding: isVisible ? '0px 5px 5px 15px' : '0px',
                  transition:
                    'width 0.4s ease-in-out, height 0.4s ease-in-out, padding 0.2s ease-in-out',
                  overflow: 'hidden',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'scale(1)' : 'scale(0.5)',
                }}
              >
                <DcTabs
                  variant="simple"
                  size="small"
                  tabs={tabChildren || []}
                  activeTab={filters.id ? String(filters.id) : null}
                  handleChange={(value) => {
                    if (setFilters) {
                      setFilters({
                        ...filters,
                        id: value ? value : null,
                      })
                    }
                  }}
                />
              </DcFlexBox>
            </DcFlexBox>
          </DcFlexBox>
        </DcFlexBox>
      </SectionContent>
    </Section>
  )
}

export const groupStatus = (state: {
  allowedStatuses?: IOrderStatus[]
}): IStatusGroup[] => {
  const groups: Record<string, IStatusGroup> = {}
  if (state.allowedStatuses) {
    state.allowedStatuses.forEach((status) => {
      if (groups[status['status_classification']]) {
        groups[status['status_classification']].children.push(status)
        groups[status['status_classification']].parent_count += status['count']
      } else {
        groups[status['status_classification']] = {
          key: Math.random(),
          children: [status],
          color: status['color'],
          name: status['status_classification'],
          parent_count: status['count'],
        }
      }
    })
  }
  const groupedStatuses: IStatusGroup[] = []
  Object.keys(groups).forEach((key) => {
    groupedStatuses.push(groups[key])
  })
  return groupedStatuses
}

export default OrderStatus
